import { useState, useCallback } from 'react';

import { useGetExternalToolCategories, useGetExternalTools } from '@/entities/external-tool';
import { useAuth } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';

import type { CategoryId } from '@/shared/constants/category-colors';

import { ORG_WIDE_SCOPE } from './constants';

type MainTab = 'overview' | 'tools' | 'categories';
type ScopeTab = string;

type BasicInfoData = { name: string; description: string; category?: CategoryId };

interface UseExternalToolsPageOptions {
  onSaveBasicInfo?: (data: BasicInfoData, workspaceId?: string) => Promise<void>;
  onScopeChange?: (scope: ScopeTab) => void;
}

export function useExternalToolsPage({
  onSaveBasicInfo,
  onScopeChange,
}: UseExternalToolsPageOptions) {
  const currentWorkspaceId = useCurrentWorkspaceId();

  const [activeTab, setActiveTab] = useState<MainTab>('overview');
  const [activeScope, setActiveScope] = useState<ScopeTab>(currentWorkspaceId ?? ORG_WIDE_SCOPE);
  const [orgRenameConfirmOpen, setOrgRenameConfirmOpen] = useState(false);
  const [pendingRenameData, setPendingRenameData] = useState<BasicInfoData | null>(null);

  const workspaces = useAuth((state) => state.signedInUser.value?.workspaces) ?? [];
  const currentWorkspace = workspaces.find((ws) => ws.id === currentWorkspaceId) ?? null;
  const otherWorkspaces = [...workspaces]
    .filter((ws) => ws.id !== currentWorkspaceId)
    .sort((a, b) => a.name.localeCompare(b.name));

  const isOrganizationTab = activeScope === ORG_WIDE_SCOPE;
  const workspaceIdParam = isOrganizationTab ? undefined : activeScope;

  const {
    data: tools = [],
    isLoading: toolsLoading,
    isFetching: toolsFetching,
  } = useGetExternalTools({ workspaceId: workspaceIdParam });

  const { data: allCategories = [], isLoading: categoriesLoading } = useGetExternalToolCategories({
    workspaceId: workspaceIdParam,
  });

  // Parent-controlled add-modal open state: panels expose
  // `addOpen`/`onAddOpenChange` instead of an imperative trigger ref.
  const [addToolOpen, setAddToolOpen] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);

  const handleScopeChange = useCallback(
    (scope: ScopeTab) => {
      setActiveScope(scope);
      onScopeChange?.(scope);
    },
    [onScopeChange],
  );

  const handleAddClick = useCallback(() => {
    if (activeTab === 'tools') setAddToolOpen(true);
    if (activeTab === 'categories') setAddCategoryOpen(true);
  }, [activeTab]);

  const handleSaveBasicInfo = useCallback(
    async (data: BasicInfoData, workspaceId?: string) => {
      if (!workspaceId && onSaveBasicInfo) {
        setPendingRenameData(data);
        setOrgRenameConfirmOpen(true);
        return;
      }
      await onSaveBasicInfo?.(data, workspaceId);
    },
    [onSaveBasicInfo],
  );

  const handleConfirmOrgRename = useCallback(async () => {
    if (pendingRenameData && onSaveBasicInfo) {
      await onSaveBasicInfo(pendingRenameData, undefined);
    }
    setOrgRenameConfirmOpen(false);
    setPendingRenameData(null);
  }, [pendingRenameData, onSaveBasicInfo]);

  const handleCancelOrgRename = useCallback(() => {
    setOrgRenameConfirmOpen(false);
    setPendingRenameData(null);
  }, []);

  return {
    // Tab & scope state
    activeTab,
    setActiveTab,
    activeScope,
    isOrganizationTab,
    handleScopeChange,
    // Workspace data
    currentWorkspace,
    otherWorkspaces,
    workspaceIdParam,
    // Tool & category data
    tools,
    toolsLoading,
    toolsFetching,
    allCategories,
    categoriesLoading,
    // Panel add-modal registration
    addToolOpen,
    setAddToolOpen,
    addCategoryOpen,
    setAddCategoryOpen,
    handleAddClick,
    // Basic info save + org-rename confirm flow
    handleSaveBasicInfo,
    orgRenameConfirmOpen,
    handleConfirmOrgRename,
    handleCancelOrgRename,
  };
}
